import { useState, useEffect, FormEvent } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Paper,
  TextInput,
  PasswordInput,
  Button,
  Title,
  Text,
  Container,
  Group,
  Stack,
  rem,
  useMantineColorScheme,
  ActionIcon,
} from '@mantine/core'
import { IconSun, IconMoon } from '@tabler/icons-react'
import { useAuthStore } from '@/store/auth'

export function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const { colorScheme, toggleColorScheme } = useMantineColorScheme()
  const dark = colorScheme === 'dark'

  const { login, isAuthenticated, isLoading } = useAuthStore()
  const [submitting, setSubmitting] = useState(false)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [errors, setErrors] = useState({ username: '', password: '' })

  useEffect(() => {
    // Redirect if already authenticated
    if (isAuthenticated) {
      const from = (location.state as any)?.from?.pathname || '/dashboard'
      navigate(from, { replace: true })
    }
  }, [isAuthenticated, navigate, location])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    // Validate
    const newErrors = {
      username: !username ? 'Username is required' : '',
      password: !password ? 'Password is required' : '',
    }
    setErrors(newErrors)

    if (newErrors.username || newErrors.password) {
      return
    }

    setSubmitting(true)
    try {
      await login(username, password)
      // Navigation will be handled by the useEffect above
    } catch (error) {
      // Error is handled in the store
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Container size={420} my={80}>
      <Group justify="flex-end" mb="lg">
        <ActionIcon
          variant="subtle"
          color="gray"
          size="lg"
          radius="md"
          onClick={() => toggleColorScheme()}
          title="Toggle color scheme"
        >
          {dark ? (
            <IconSun style={{ width: rem(20), height: rem(20) }} stroke={1.5} />
          ) : (
            <IconMoon style={{ width: rem(20), height: rem(20) }} stroke={1.5} />
          )}
        </ActionIcon>
      </Group>

      <Title ta="center" mb="xs">
        <Text
          component="span"
          size="xl"
          fw={900}
          gradient={{ from: 'blue', to: 'cyan', deg: 45 }}
          variant="gradient"
        >
          RustSwarm
        </Text>
      </Title>

      <Text c="dimmed" size="sm" ta="center" mb={30}>
        Sign in to access the dashboard
      </Text>

      <Paper withBorder shadow="md" p={30} radius="md">
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            <TextInput
              label="Username"
              placeholder="Your username"
              required
              value={username}
              onChange={(e) => setUsername(e.currentTarget.value)}
              error={errors.username}
            />

            <PasswordInput
              label="Password"
              placeholder="Your password"
              required
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              error={errors.password}
            />

            <Button
              type="submit"
              fullWidth
              loading={submitting || isLoading}
              mt="md"
            >
              Sign in
            </Button>
          </Stack>
        </form>

        <Text c="dimmed" size="xs" ta="center" mt="xl">
          RustSwarm v0.1.0 - Container Orchestration Platform
        </Text>
      </Paper>
    </Container>
  )
}

export default Login
